/**
 * WHAT THE HOSTED PRODUCT DOES NOT HAVE, in one list that both the app and the assistant read.
 *
 * ⚠ **It exists because a support assistant grounded in `DOCS.md` recommended a page that is not
 * there** (owner, 2026-08-09, testing the chat in saas: *"it is talking about the 'discover outlets'
 * page, which is not available on the SaaS app"*). The corpus describes the product as built, the
 * hosted edition hides a surface or two, and the model had no way to know - so it confidently sent a
 * paying customer to a nav item they do not have, which is worse than declining.
 *
 * ⚠ **The obvious fix was a sentence in the prompt, and that is the version that goes stale.** A
 * hand-written "there is no Discover page in the hosted product" is correct the day it is typed and
 * silently wrong the next time a surface is hidden - and nothing would fail, because the symptom is a
 * chatbot being helpful about the wrong thing. So the LIST is the source: the app removes what this
 * names, and the prompt says what this names. **When two places must agree about an absence, make one
 * of them the reason for the other.**
 *
 * ⚠ **`instead` is prose read by a MODEL, which is why it is allowed to live in `shared/`.**
 * `constants.ts`'s rule is to ask who reads a string before putting one here: this reader is an LLM
 * composing an answer, the same sanctioned non-`t()` reader `services/ai.ts` writes for. It is never
 * rendered to anybody, so it is not an i18n leak - but it IS read by a customer at one remove, so it
 * has to be true and it has to name where they should go instead.
 */
export interface AbsentSurface {
	/** The `data-page` the nav item carries, which is also what removes it. */
	page: string;
	/** What the docs and the app call it, so the model recognizes what it is being asked about. */
	label: string;
	/** What a hosted tenant should do instead. Read by the assistant; never rendered. */
	instead: string;
}

export const SAAS_ABSENT_SURFACES: AbsentSurface[] = [
	{
		page: "discover",
		// Read by the support MODEL and never rendered: it has to match the word in DOCS.md and on the
		// nav item so the assistant recognizes what it is being asked about, and a translated label
		// would stop matching the corpus it exists to correct.
		// i18n-ignore: model-facing, must match DOCS.md and the nav item verbatim
		label: "Discover",
		// The long WHY is above the removal in `src/frontend/app.ts`: outlet discovery is the priciest
		// AI lane in the app, so the operator runs it once into the catalog every tenant reads.
		instead:
			"outlets are found and checked centrally by the Get Heard team and appear on the Outlets page, so there is nothing for the customer to run"
	}
];
